import React from 'react';
import withMainLayout from 'layouts/main';
import dynamic from 'next/dynamic';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {SectionPadding, SectionPaddingHalfTop, SectionPaddingHalfBottom} from '@/section';
import {H1, H2, P, PMarginSm} from '@/typography';
import {BreakVisibleXxl} from '@/breaks';
import {withGreen800, withBgNeutral200Rounded} from '@/hocs/withColors';
import {withMarginBottomZeroToMd} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import {ListOlNumbered, ListDisc} from '@/ui/lists/list-icons';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';

const H1Green800 = withGreen800(H1);
const DivBgNeutral200Rounded = withBgNeutral200Rounded('div');
const DivMarginedMobile = withMarginBottomZeroToMd('div');
const imgPath = '/static/images/developers/hris-onboarding/';

const SubmitPartnerApplication = dynamic(
  import('@/content/developers/submit-partner-application'),
  {
    loading: () => null
  }
);

const seoMetas = {
  title: 'HRIS & onboarding integration | Workable developer partners',
  description:
    'Integrate your HRIS or onboarding platform with Workable. Help your customers export candidate data directly from Workable to your system.',
  canonical: 'https://www.workable.com/developers/partner-program/hris-onboarding'
};

const HrisOnboardingPartnerPage = props => (
  <>
    <SectionPaddingHalfBottom>
      <Container variant="section-padding-below-nav">
        <SectionPaddingHalfTop>
          <Grid fluid>
            <Row middle="lg">
              <Col xs={12} lg={6}>
                <H1Green800 className="heading-1-smaller">
                  Connect your HRIS or
                  <BreakVisibleXxl /> onboarding tool with Workable
                </H1Green800>
                <PMarginSm>
                  As candidates move through the hiring process, their profiles gather valuable
                  information. Connect your service with Workable to let this data flow directly to
                  your customers' accounts.
                </PMarginSm>
                <PMarginSm>
                  Companies use Workable to track candidates through hiring. Each candidate profile
                  becomes rich with data as they progress.
                </PMarginSm>
                <DivMarginedMobile>
                  <ListDisc>
                    <li>Photo or avatar</li>
                    <li>First and last name</li>
                    <li>Personal email address</li>
                    <li>Job title</li>
                    <li>Start date</li>
                    <li>Resume (PDF)</li>
                    <li>LinkedIn url</li>
                    <li>User status</li>
                  </ListDisc>
                </DivMarginedMobile>
              </Col>
              <Col xs={12} lg={6}>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="onboarding-hero"
                  imageTypeFallback="jpg"
                  alt="Connect your HRIS or onboarding tool with Workable - image"
                  fetchpriority="high"
                  loading="eager"
                  width={584}
                  height={624}
                />
              </Col>
            </Row>
          </Grid>
        </SectionPaddingHalfTop>
      </Container>
    </SectionPaddingHalfBottom>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="connect-onboarding"
                  imageTypeFallback="jpg"
                  alt="How customers connect Workable with HRIS or Onboarding tools - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <H2 className="heading-3">
                How customers connect Workable with HRIS or Onboarding tools
              </H2>
              <PMarginSm>
                Workable users can activate your integration with a one-time setup:
              </PMarginSm>
              <DivMarginedMobile>
                <DivBgNeutral200Rounded>
                  <ListOlNumbered>
                    <li>They find it in the integrations list inside Workable</li>
                    <li>Existing users of both services follow simple on-screen instructions</li>
                    <li>New users see your logo, a description, and a link to learn more</li>
                  </ListOlNumbered>
                </DivBgNeutral200Rounded>
              </DivMarginedMobile>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={5}>
              <H2 className="heading-3">Sending data from Workable</H2>
              <PMarginSm>
                After connecting, data exports happen automatically when candidates reach the hired
                stage. Your service becomes part of the normal journey from candidate to employee.
              </PMarginSm>
            </Col>
            <Col xs={12} lg={6} lgOffset={1}>
              <GridLayout Col={1} autoRows>
                <GridItem>
                  <BoxGrid
                    backgroundColor="var(--brown50)"
                    Url="https://workable.readme.io/"
                    UrlCopy="View API documentation"
                    target="_blank"
                    minHeight={288}>
                    <PMarginSm className="heading-3">API docs</PMarginSm>
                    <P>
                      See how you could start to integrate your product or service with Workable
                    </P>
                  </BoxGrid>
                </GridItem>
              </GridLayout>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>
    <SubmitPartnerApplication />
  </>
);

export default withMainLayout(HrisOnboardingPartnerPage, {...seoMetas});

export {getSharedPageStaticProps as getStaticProps} from 'helpers/sharedPageStaticProps';
